import { FieldGuideConnectionStatusKind } from '../../Models/Api/strongbox.financialportal';

// The state machine for the signed-in user's FieldGuide connection. NotConnected is the state with no
// connection row (or one the user hasn't completed yet); Connecting is only entered while a connect
// call is in flight. ReconnectRequired mirrors the FieldGuideConnectionStatus the API can return for an
// existing connection whose credential needs replacing.
export enum FieldGuideConnectionState {
    NotConnected = 'NotConnected',
    Connecting = 'Connecting',
    Connected = 'Connected',
    ReconnectRequired = 'ReconnectRequired',
}

export enum FieldGuideActions {
    BeginConnect = 'FIELDGUIDE/BeginConnect',
    ConnectSucceeded = 'FIELDGUIDE/ConnectSucceeded',
    ConnectFailed = 'FIELDGUIDE/ConnectFailed',
    SetReconnectRequired = 'FIELDGUIDE/SetReconnectRequired',
    ConnectionStatusLoaded = 'FIELDGUIDE/ConnectionStatusLoaded',
    BeginUpload = 'FIELDGUIDE/BeginUpload',
    SelectCompany = 'FIELDGUIDE/SelectCompany',
    SelectEngagement = 'FIELDGUIDE/SelectEngagement',
    SetRequestMapping = 'FIELDGUIDE/SetRequestMapping',
    NextUploadStep = 'FIELDGUIDE/NextUploadStep',
    PreviousUploadStep = 'FIELDGUIDE/PreviousUploadStep',
    BeginSending = 'FIELDGUIDE/BeginSending',
    SendSucceeded = 'FIELDGUIDE/SendSucceeded',
    SendFailed = 'FIELDGUIDE/SendFailed',
    CancelUpload = 'FIELDGUIDE/CancelUpload',
}

export enum FieldGuideUploadState {
    SelectingCompany = 'SelectingCompany',
    SelectingEngagement = 'SelectingEngagement',
    MappingRequests = 'MappingRequests',
    Reviewing = 'Reviewing',
    Sending = 'Sending',
    Succeeded = 'Succeeded',
    Failed = 'Failed',
}

export interface FieldGuideCompanySelection { readonly id: string; readonly name: string; }
export interface FieldGuideEngagementSelection { readonly id: string; readonly name: string; }
export interface FieldGuideMappedRequest { readonly id: string; readonly title: string; readonly requestNumber: string; readonly description: string; }
export interface FieldGuideWorkbookMapping { readonly request: FieldGuideMappedRequest | undefined; readonly fileName: string; }

export interface IBeginUpload { readonly type: FieldGuideActions.BeginUpload; }
export interface ISelectCompany { readonly type: FieldGuideActions.SelectCompany; readonly company: FieldGuideCompanySelection; }
export interface ISelectEngagement { readonly type: FieldGuideActions.SelectEngagement; readonly engagement: FieldGuideEngagementSelection; }
export interface ISetRequestMapping {
    readonly type: FieldGuideActions.SetRequestMapping;
    readonly variantId: string;
    readonly request: FieldGuideMappedRequest | undefined;
    readonly fileName: string;
}
export interface INextUploadStep { readonly type: FieldGuideActions.NextUploadStep; }
export interface IPreviousUploadStep { readonly type: FieldGuideActions.PreviousUploadStep; }
export interface IBeginSending { readonly type: FieldGuideActions.BeginSending; }
export interface ISendSucceeded { readonly type: FieldGuideActions.SendSucceeded; }
export interface ISendFailed { readonly type: FieldGuideActions.SendFailed; }
export interface ICancelUpload { readonly type: FieldGuideActions.CancelUpload; }

export interface IBeginConnect {
    readonly type: FieldGuideActions.BeginConnect;
}

export interface IConnectSucceeded {
    readonly type: FieldGuideActions.ConnectSucceeded;
    readonly fieldGuideEmail: string | undefined;
    readonly fieldGuideDisplayName: string | undefined;
}

// The connect call failed (a rejected token, FieldGuide/portal unavailability, or an unexpected
// failure). Returns the connection to NotConnected; the rejection's message is shown by the caller
// inline and is not itself part of this domain's state.
export interface IConnectFailed {
    readonly type: FieldGuideActions.ConnectFailed;
}

// An existing connection (of either credential type) needs the user to reconnect.
export interface ISetReconnectRequired {
    readonly type: FieldGuideActions.SetReconnectRequired;
}

// The server-reported connection status, read on wizard entry. `reason` is only meaningful with
// ReconnectRequired; the account fields only with Connected.
export interface IConnectionStatusLoaded {
    readonly type: FieldGuideActions.ConnectionStatusLoaded;
    readonly status: FieldGuideConnectionStatusKind;
    readonly reason: string | undefined;
    readonly fieldGuideEmail: string | undefined;
    readonly fieldGuideDisplayName: string | undefined;
}

export type KnownAction =
    IBeginConnect |
    IConnectSucceeded |
    IConnectFailed |
    ISetReconnectRequired |
    IBeginUpload |
    ISelectCompany |
    ISelectEngagement |
    ISetRequestMapping |
    INextUploadStep |
    IPreviousUploadStep |
    IBeginSending |
    ISendSucceeded |
    ISendFailed |
    ICancelUpload |
    IConnectionStatusLoaded;
